import * as React from "react"
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader as PrimitiveTableHeader,
    TableRow,
} from "./table"
import DataTableMobile from "./data-table-mobile";
import {useColumnSizingManager} from "./use-column-sizing-manager";
import {classNames as cn} from "../../utils/Utils";

import {useEffect} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {useTranslation} from "react-i18next";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import Pagination from "../common/table/pagination";
import TableToolbar from "../common/table/table-toolbar";
import Loader from "../common/loader/loader";
import FormPopup from "../common/popup/form-popup";
import DragIcon from "../icons/drag-icon";
import {ArrowDownIcon, ArrowUpIcon, ViewListIcon, XIcon, SwitchVerticalIcon} from "@heroicons/react/outline";

const isBrowser = typeof window !== "undefined";

const buildLocalStorageKey = (listKey, suffix) => {
    if (!listKey) {
        return null;
    }
    return `data-table:${listKey}:${suffix}`;
};

const loadFromStorage = (key, fallback) => {
    if (!key || !isBrowser) {
        return fallback;
    }
    try {
        const stored = window.localStorage.getItem(key);
        return stored ? JSON.parse(stored) : fallback;
    } catch (error) {
        return fallback;
    }
};

const saveToStorage = (key, value) => {
    if (!key || !isBrowser) {
        return;
    }
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // ignore storage errors
    }
};

const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list ?? []);
    const [removed] = result.splice(startIndex, 1);
    if (removed === undefined) {
        return result;
    }
    result.splice(endIndex, 0, removed);
    return result;
};

const mergeColumnOrder = (baseOrder, nextOrder) => {
    const base = Array.isArray(baseOrder) ? baseOrder.map((id) => String(id)) : [];
    if (base.length === 0) {
        return [];
    }
    const seen = new Set();
    const sanitizedNext = Array.isArray(nextOrder)
        ? nextOrder
            .map((id) => String(id))
            .filter((id) => base.includes(id))
            .filter((id) => {
                if (seen.has(id)) {
                    return false;
                }
                seen.add(id);
                return true;
            })
        : [];
    const missing = base.filter((id) => !seen.has(id));
    return [...sanitizedNext, ...missing];
};

const arraysEqual = (a, b) => {
    if (a === b) {
        return true;
    }
    if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let index = 0; index < a.length; index += 1) {
        if (a[index] !== b[index]) {
            return false;
        }
    }
    return true;
};

const normalizeOffset = (value) => {
    if (value === undefined || value === null) {
        return undefined;
    }
    return typeof value === "number" ? `${value}px` : value;
};

const PINNED_CELL_Z_INDEX = 5;
const PINNED_HEADER_Z_INDEX = 30;
const STICKY_HEADER_WRAPPER_Z_INDEX = PINNED_HEADER_Z_INDEX + 10;

const getCommonPinningStyles = (column, options = {}) => {
    if (!column) {
        return {};
    }
    const {isHeader = false} = options;
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
        isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
        isPinned === "right" && column.getIsFirstColumn("right");
    const columnLeftOverride =
        column.columnDef?.left ?? column.columnDef?.meta?.left;
    const columnRightOverride =
        column.columnDef?.right ?? column.columnDef?.meta?.right;
    const size = column.getSize();
    const columnDef = column.columnDef ?? {};
    const minWidth =
        columnDef.meta?.minWidth ??
        (typeof columnDef.minSize === "number" ? columnDef.minSize : size);
    const maxWidth =
        columnDef.meta?.maxWidth ??
        (typeof columnDef.maxSize === "number" ? columnDef.maxSize : undefined);
    const zIndex = isPinned
        ? (isHeader ? PINNED_HEADER_Z_INDEX : PINNED_CELL_Z_INDEX)
        : undefined;

    return {
        boxShadow: isLastLeftPinnedColumn
            ? "-3px 0 3px -4px gray inset"
            : isFirstRightPinnedColumn
                ? "3px 0 3px -4px gray inset"
                : undefined,

        paddingRight: isFirstRightPinnedColumn ? "20px" : undefined,
        left:
            normalizeOffset(columnLeftOverride) ??
            (isPinned === "left" ? `${column.getStart("left")}px` : undefined),
        right:
            normalizeOffset(columnRightOverride) ??
            (isPinned === "right"
                ? `${column.getAfter("right")}px`
                : undefined),
        position: isPinned ? "sticky" : "",
        width: size,
        minWidth,
        ...(typeof maxWidth === "number" ? {maxWidth} : {}),
        zIndex,

    };
};

export function DataTable({
    columns,
    data,
    onSearch = null,
    onSorting = null,
    leftPinning = [],
    rightPinning = [],
    cleanRows = false,
    onRowSelected = null,
    activePagination = true,
    onClickRow = null,
    className = '',
    isLoading = false,
    onChangeLimitOffset = null,
    limit = 10,
    total = null,
    offset = 0,
    paginationMode,
    isFetchingMore = false,
    onFetchingMore = null,
    infiniteScrollLoader = null,
    showHeader = true,
    showSearch = undefined,
    searchLength = 3,
    showShippingType = false,
    shippingTypes = [],
    shippingType = null,
    setShippingType = () => {},
    isDynamicSearch = false,
    title = '',
    totalRows = null,
    activeDynamicFilter = false,
    fixedHeight = '',
    fixedHeightFilter = '',
    listKey = null,
    enableColumnVisibility = false,
    enableColumnReorder = false,
    initialColumnVisibility = null,
    children,
    }) {

    const isMobile = false;
    const { t } = useTranslation();

    const columnVisibilityStorageKey = React.useMemo(() => (
        enableColumnVisibility ? buildLocalStorageKey(listKey, "column-visibility") : null
    ), [enableColumnVisibility, listKey]);

    const memoInitialVisibility = React.useMemo(() => initialColumnVisibility ?? {}, [initialColumnVisibility]);

    const [columnVisibility, setColumnVisibility] = React.useState(() => {
        if (!enableColumnVisibility) {
            return memoInitialVisibility;
        }
        const stored = loadFromStorage(columnVisibilityStorageKey, null);
        if (stored) {
            return {...memoInitialVisibility, ...stored};
        }
        return memoInitialVisibility;
    });

    const columnSizingStorageKey = React.useMemo(
        () => buildLocalStorageKey(listKey, "column-sizing"),
        [listKey]
    );

    const initialColumnSizing = React.useMemo(
        () => (columnSizingStorageKey ? loadFromStorage(columnSizingStorageKey, {}) : {}),
        [columnSizingStorageKey]
    );

    const {
        columnSizing,
        setColumnSizing: handleColumnSizingChange,
        replaceColumnSizing,
        flushPendingSizing: flushPendingColumnSizing,
    } = useColumnSizingManager(initialColumnSizing);

    useEffect(() => {
        replaceColumnSizing(initialColumnSizing);
    }, [initialColumnSizing, replaceColumnSizing]);

    const lastSavedColumnSizingRef = React.useRef({
        key: columnSizingStorageKey,
        value: columnSizing,
    });

    const columnOrderStorageKey = React.useMemo(() => (
        enableColumnReorder ? buildLocalStorageKey(listKey, "column-order") : null
    ), [enableColumnReorder, listKey]);

    const defaultColumnOrder = React.useMemo(() => {
        const ids = [];
        const visit = (definitions) => {
            (definitions ?? []).forEach((definition) => {
                if (!definition) {
                    return;
                }
                if (Array.isArray(definition.columns)) {
                    visit(definition.columns);
                    return;
                }
                const resolvedId = definition.id ?? definition.accessorKey;
                if (resolvedId === undefined || resolvedId === null) {
                    return;
                }
                const stringId = String(resolvedId);
                if (!ids.includes(stringId)) {
                    ids.push(stringId);
                }
            });
        };
        visit(columns ?? []);
        return ids;
    }, [columns]);

    const [columnOrder, setColumnOrder] = React.useState(defaultColumnOrder);
    const columnOrderLoadedRef = React.useRef(false);

    const [showColumnMenu, setShowColumnMenu] = React.useState(false);
    const columnMenuRef = React.useRef(null);
    const [isColumnReorderOpen, setIsColumnReorderOpen] = React.useState(false);

    useEffect(() => {
        if (!enableColumnReorder) {
            columnOrderLoadedRef.current = false;
            return;
        }
        if (columnOrderLoadedRef.current) {
            return;
        }
        if (!columnOrderStorageKey) {
            columnOrderLoadedRef.current = true;
            return;
        }
        const storedOrder = loadFromStorage(columnOrderStorageKey, null);
        if (Array.isArray(storedOrder) && storedOrder.length > 0) {
            setColumnOrder((prev) => mergeColumnOrder(defaultColumnOrder, storedOrder ?? prev));
        }
        columnOrderLoadedRef.current = true;
    }, [enableColumnReorder, columnOrderStorageKey, defaultColumnOrder]);

    useEffect(() => {
        setColumnOrder((prev) => {
            const nextOrder = mergeColumnOrder(defaultColumnOrder, prev);
            return arraysEqual(prev, nextOrder) ? prev : nextOrder;
        });
    }, [defaultColumnOrder]);

    useEffect(() => {
        if (!enableColumnReorder) {
            return;
        }
        if (!columnOrderStorageKey) {
            return;
        }
        if (!columnOrderLoadedRef.current) {
            return;
        }
        if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
            return;
        }
        saveToStorage(columnOrderStorageKey, columnOrder);
    }, [columnOrder, enableColumnReorder, columnOrderStorageKey]);

    // SORTING
    const [sorting, setSorting] = React.useState([])
    const previousSortingRef = React.useRef([])
    const [columnFilters, setColumnFilters] = React.useState([])

    const [rowSelection, setRowSelection] = React.useState({})
    const [columnPinning, setColumnPinning] = React.useState({
        left: isMobile ? []: leftPinning,
        right: isMobile ? []: rightPinning
    })

    const resolvedMode = React.useMemo(() => {
        if (paginationMode) {
            return paginationMode;
        }
        if (!activePagination && onFetchingMore) {
            return 'infinite';
        }
        return 'pagination';
    }, [paginationMode, activePagination, onFetchingMore]);

    const isInfinite = resolvedMode === 'infinite';
    const showPagination = resolvedMode === 'pagination' && activePagination;

    const scrollContainerId = React.useMemo(() => `data-table-scroll-${Math.random().toString(36).slice(2)}`, []);


    useEffect(() => {
        if(isMobile){
            setColumnPinning({
                left: [],
                right: []
            });
        }
        // eslint-disable-next-line
    }, [isMobile]);

    useEffect(() => {
        if (!enableColumnVisibility) {
            setShowColumnMenu(false);
            return;
        }
        if (!initialColumnVisibility || Object.keys(initialColumnVisibility).length === 0) {
            return;
        }
        setColumnVisibility((prev) => {
            const nextVisibility = {...prev};
            let hasChanges = false;

            Object.entries(initialColumnVisibility).forEach(([columnId, defaultValue]) => {
                if (!(columnId in nextVisibility)) {
                    nextVisibility[columnId] = defaultValue;
                    hasChanges = true;
                }
            });

            return hasChanges ? nextVisibility : prev;
        });
    }, [enableColumnVisibility, initialColumnVisibility]);

    useEffect(() => {
        if (!enableColumnVisibility || !columnVisibilityStorageKey) {
            return;
        }
        saveToStorage(columnVisibilityStorageKey, columnVisibility);
    }, [columnVisibility, columnVisibilityStorageKey, enableColumnVisibility]);

    useEffect(() => {
        if (!showColumnMenu) {
            return undefined;
        }
        const handleClickOutside = (event) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
                setShowColumnMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showColumnMenu]);


    useEffect( () => {
        if (onRowSelected) {
            const selectedData = Object.keys(rowSelection)
                .map(index => data[parseInt(index)])
                .filter(Boolean); // per sicurezza, in caso qualche index non esista

            onRowSelected(selectedData)
        }
        // eslint-disable-next-line
    }, [rowSelection]);


    useEffect( () => {
        if(cleanRows && Object.keys(rowSelection).length > 0){
            setRowSelection({})
        }
        // eslint-disable-next-line
    }, [cleanRows]);

    const handleColumnOrderChange = React.useCallback((nextOrder) => {
        if (!enableColumnReorder) {
            return;
        }
        setColumnOrder(mergeColumnOrder(defaultColumnOrder, nextOrder));
    }, [enableColumnReorder, defaultColumnOrder]);

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: enableColumnVisibility ? setColumnVisibility : undefined,
        onColumnSizingChange: handleColumnSizingChange,
        onColumnOrderChange: enableColumnReorder ? handleColumnOrderChange : undefined,
        manualSorting: Boolean(onSorting),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onRowSelectionChange: setRowSelection,
        onColumnPinningChange: setColumnPinning,
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        columnResizeDirection: "ltr",
        state: {
            sorting,
            columnFilters,
            rowSelection,
            columnPinning,
            columnVisibility,
            columnSizing,
            ...(enableColumnReorder ? {columnOrder} : {}),
            pagination: {
                pageIndex: 0,
                pageSize: total ?? data.length,
            },
        },
    })
    const columnSizingInfo = table.getState().columnSizingInfo;
    const isColumnCurrentlyResizing = Boolean(columnSizingInfo?.isResizingColumn);

    useEffect(() => {
        if (!isColumnCurrentlyResizing) {
            flushPendingColumnSizing();
        }
    }, [isColumnCurrentlyResizing, flushPendingColumnSizing]);

    useEffect(() => {
        if (!columnSizingStorageKey) {
            lastSavedColumnSizingRef.current = {
                key: columnSizingStorageKey,
                value: columnSizing,
            };
            return;
        }
        if (isColumnCurrentlyResizing) {
            return;
        }
        const {key: previousKey, value: previousValue} = lastSavedColumnSizingRef.current;
        if (previousKey === columnSizingStorageKey && previousValue === columnSizing) {
            return;
        }
        saveToStorage(columnSizingStorageKey, columnSizing);
        lastSavedColumnSizingRef.current = {
            key: columnSizingStorageKey,
            value: columnSizing,
        };
    }, [columnSizing, columnSizingStorageKey, isColumnCurrentlyResizing]);

    useEffect(() => {
        if (typeof onSorting !== "function") {
            return;
        }
        const previous = previousSortingRef.current;
        previousSortingRef.current = sorting;

        if (!Array.isArray(sorting) || sorting.length === 0) {
            if (!Array.isArray(previous) || previous.length === 0) {
                return;
            }
            onSorting(null);
            return;
        }

        const [first] = sorting;
        if (!first || first.id === undefined || first.id === null) {
            return;
        }

        const column = table?.getColumn(first.id);
        const sortKeyRaw = column?.columnDef?.meta?.sortKey ?? first.id;
        if (sortKeyRaw === undefined || sortKeyRaw === null) {
            return;
        }

        onSorting({
            columnId: String(first.id),
            sortKey: String(sortKeyRaw),
            sortDir: first.desc ? "DESC" : "ASC",
        });
    }, [sorting, onSorting, table]);

    useEffect(() => {
        if (!enableColumnReorder) {
            return;
        }
        const leafColumns = table.getAllLeafColumns();
        if (!leafColumns || leafColumns.length === 0) {
            return;
        }
        const ids = leafColumns.map((column) => String(column.id));
        setColumnOrder((prev) => {
            const nextOrder = mergeColumnOrder(ids, prev);
            return arraysEqual(prev, nextOrder) ? prev : nextOrder;
        });
    }, [enableColumnReorder, table]);

    const totalItems = total ?? data.length
    const headerTotalRows = totalRows ?? totalItems
    const pageSize =  limit

    const hasMore = React.useMemo(() => {
        if (!isInfinite) {
            return false;
        }
        if (total === null || total === undefined) {
            return true;
        }
        return data.length < total;
    }, [isInfinite, total, data.length]);

    const handleFetchMore = React.useCallback(() => {
        if (!isInfinite || !onFetchingMore || isFetchingMore || !hasMore) {
            return;
        }
        const nextOffset = offset + pageSize;
        onFetchingMore(nextOffset);
    }, [isInfinite, onFetchingMore, isFetchingMore, hasMore, offset, pageSize]);

    const infiniteLoader = React.useMemo(() => {
        if (infiniteScrollLoader !== null && infiniteScrollLoader !== undefined) {
            return infiniteScrollLoader;
        }
        if (!isFetchingMore) {
            return null;
        }
        return (
            <div className="py-4 text-center text-sm text-gray-500">
                <Loader />
            </div>
        );
    }, [infiniteScrollLoader, isFetchingMore]);

    const resolvedShowSearch = showSearch === undefined ? Boolean(onSearch) : showSearch;
    const hasActiveDynamicFilter = Array.isArray(activeDynamicFilter)
        ? activeDynamicFilter.length > 0
        : Boolean(activeDynamicFilter);

    const containerHeightClass = hasActiveDynamicFilter && fixedHeightFilter
        ? fixedHeightFilter
        : fixedHeight;

    const scrollContainerClass = cn(
        "",
        containerHeightClass,
        className
    );

    const resolveColumnLabel = React.useCallback((column) => {
        const headerDef = column.columnDef.header;
        if (typeof headerDef === "string") {
            return headerDef;
        }
        if (typeof column.columnDef.title === "string") {
            return column.columnDef.title;
        }
        if (typeof column.columnDef.meta?.title === "string") {
            return column.columnDef.meta.title;
        }
        return column.id;
    }, []);

    const columnReorderItems = React.useMemo(() => {
        if (!enableColumnReorder) {
            return [];
        }
        const leafColumns = table.getAllLeafColumns();
        if (!leafColumns || leafColumns.length === 0) {
            return [];
        }
        const columnMap = new Map();
        leafColumns.forEach((column) => {
            const pinState = column.getIsPinned();
            if (pinState === 'left') {
                return;
            }
            if (String(column.id) === 'actions') {
                return;
            }
            columnMap.set(String(column.id), column);
        });
        const orderedIds = mergeColumnOrder(Array.from(columnMap.keys()), columnOrder);
        const visibilityState = columnVisibility ?? {};
        return orderedIds
            .map((columnId) => {
                const column = columnMap.get(columnId);
                if (!column) {
                    return null;
                }
                const visibilityKey = String(column.id);
                const isVisible = Object.prototype.hasOwnProperty.call(visibilityState, visibilityKey)
                    ? visibilityState[visibilityKey] !== false
                    : column.getIsVisible();
                return {
                    id: column.id,
                    label: resolveColumnLabel(column),
                    isVisible,
                };
            })
            .filter(Boolean);
    }, [enableColumnReorder, table, columnOrder, resolveColumnLabel, columnVisibility]);

    const reorderableColumnIds = React.useMemo(() => columnReorderItems.map((item) => String(item.id)), [columnReorderItems]);

    const handleOpenColumnReorder = React.useCallback(() => {
        setShowColumnMenu(false);
        setIsColumnReorderOpen(true);
    }, [setShowColumnMenu, setIsColumnReorderOpen]);

    const handleColumnReorderDragEnd = React.useCallback((result) => {
        if (!enableColumnReorder) {
            return;
        }
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        if (reorderableColumnIds.length <= 1) {
            return;
        }
        setColumnOrder((prev) => {
            const base = mergeColumnOrder(defaultColumnOrder, prev);
            const reorderableSet = new Set(reorderableColumnIds);
            const currentOrder = base.filter((id) => reorderableSet.has(String(id)));
            if (currentOrder.length <= 1) {
                return base;
            }
            const nextOrderSubset = reorderArray(currentOrder, result.source.index, result.destination.index);
            let subsetIndex = 0;
            const nextBase = base.map((id) => {
                if (!reorderableSet.has(String(id))) {
                    return id;
                }
                const nextId = nextOrderSubset[subsetIndex];
                subsetIndex += 1;
                return nextId;
            });
            return arraysEqual(prev, nextBase) ? prev : nextBase;
        });
    }, [enableColumnReorder, defaultColumnOrder, reorderableColumnIds]);

    const columnToggleNode = React.useMemo(() => {
        if (!enableColumnVisibility) {
            return null;
        }
        const hideableColumns = table
            .getAllLeafColumns()
            .filter((column) => column.getCanHide());
        const hasVisibilityOptions = hideableColumns.length > 0;
        const hasReorderOption = enableColumnReorder && columnReorderItems.length > 1;
        if (!hasVisibilityOptions && !hasReorderOption) {
            return null;
        }
        const visibilityState = columnVisibility ?? {};

        return (
            <div ref={columnMenuRef} className="relative">
                <button
                    type="button"
                    onClick={() => setShowColumnMenu((prev) => !prev)}
                    className="inline-flex h-9 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100"
                    aria-label={t("app.common.columns", {defaultValue: "Colonne"})}
                    title={t("app.common.columns", {defaultValue: "Colonne"})}
                >
                    <ViewListIcon className="h-4 w-4" />
                </button>
                {showColumnMenu && (
                    <div className="absolute right-0 z-20 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg">
                        <div className="flex items-center justify-between px-3 py-2">
                            <span className="text-sm font-medium text-gray-700">
                                {t("app.common.visible_columns", {defaultValue: "Visible columns"})}
                            </span>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => setShowColumnMenu(false)}
                                aria-label={t("app.common.close", {defaultValue: "Close"})}
                            >
                                <XIcon className="h-4 w-4" />
                            </button>
                        </div>
                        {hasVisibilityOptions && (
                            <div className="max-h-64 overflow-auto py-2 text-sm text-gray-700">
                                {hideableColumns.map((column) => {
                                    const columnId = String(column.id);
                                    const isChecked = Object.prototype.hasOwnProperty.call(visibilityState, columnId)
                                        ? visibilityState[columnId] !== false
                                        : column.getIsVisible();
                                    return (
                                        <label
                                            key={column.id}
                                            className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(event) => column.toggleVisibility(event.target.checked)}
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span>{resolveColumnLabel(column)}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        {hasReorderOption && (
                            <div className={cn("border-t border-gray-200 px-3 py-2", !hasVisibilityOptions && "pt-3")}>
                                <button
                                    type="button"
                                    onClick={handleOpenColumnReorder}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-blue-600 hover:bg-gray-50"
                                >
                                    <SwitchVerticalIcon className="h-4 w-4" />
                                    {t("app.common.reorder_columns", {defaultValue: "Riordina colonne"})}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [
        enableColumnVisibility,
        enableColumnReorder,
        showColumnMenu,
        table,
        t,
        columnReorderItems,
        columnVisibility,
        handleOpenColumnReorder,
        resolveColumnLabel,
    ]);

    const toolbarChildren = React.useMemo(() => {
        if (!enableColumnVisibility || !columnToggleNode) {
            return children;
        }

        if (!children) {
            return columnToggleNode;
        }

        if (hasActiveDynamicFilter) {
            return (
                <div className="flex items-center gap-2">
                    {columnToggleNode}
                    {children}
                </div>
            );
        }

        return (
            <>
                {columnToggleNode}
                {children}
            </>
        );
    }, [children, columnToggleNode, enableColumnVisibility, hasActiveDynamicFilter]);

    const renderHeaderContent = React.useCallback((header) => {
        if (header.isPlaceholder) {
            return null;
        }

        const canSort = header.column.getCanSort();
        const sortingState = header.column.getIsSorted();
        const headerContent = flexRender(header.column.columnDef.header, header.getContext());

        if (!canSort) {
            return headerContent;
        }

        const toggleSortingHandler = header.column.getToggleSortingHandler();
        const SortingIcon = sortingState === "asc" ? ArrowUpIcon : sortingState === "desc" ? ArrowDownIcon : SwitchVerticalIcon;

        return (
            <button
                type="button"
                onClick={toggleSortingHandler}
                className="group flex w-full items-center uppercase gap-2 text-left focus:outline-none"
            >
                <span className="flex-1 truncate group-hover:text-blue-900">
                    {headerContent}
                </span>
                <SortingIcon
                    className={cn(
                        "h-3 w-3 flex-none text-gray-400 transition-colors",
                        sortingState && "text-blue-600"
                    )}
                    aria-hidden="true"
                />
            </button>
        );
    }, []);

    const resolveAriaSort = React.useCallback((header) => {
        const canSort = header.column.getCanSort();
        if (!canSort) {
            return undefined;
        }
        const sortingState = header.column.getIsSorted();
        if (sortingState === "asc") {
            return "ascending";
        }
        if (sortingState === "desc") {
            return "descending";
        }
        return "none";
    }, []);


    return (
        <div className="w-full overflow-x-hidden">

            {showHeader && (
                <TableToolbar
                    onSearch={onSearch}
                    showSearch={resolvedShowSearch && Boolean(onSearch)}
                    searchLength={searchLength}
                    showShippingType={showShippingType}
                    shippingTypes={shippingTypes}
                    shippingType={shippingType}
                    setShippingType={setShippingType}
                    isDynamicSearch={isDynamicSearch}
                    title={title}
                    totalRows={headerTotalRows}
                    activeDynamicFilter={hasActiveDynamicFilter}
                >
                    {toolbarChildren}
                </TableToolbar>
            )}

            <div  className={scrollContainerClass}
                  id={scrollContainerId}
                  style={{
                     overflow: 'auto', //our scrollable table container
                     position: 'relative', //needed for sticky header
                 }}
            >
                {isInfinite ? (
                    <InfiniteScroll
                        dataLength={data.length }
                        next={handleFetchMore}
                        hasMore={hasMore}
                        loader={infiniteLoader}
                        scrollableTarget={scrollContainerId}
                        style={{height: 'none', overflow: 'none'}}
                    >
                        <DataTableMobile
                            table={table}
                            isLoading={isLoading}
                            resolveColumnLabel={resolveColumnLabel}
                            onClickRow={onClickRow}
                        />

                        <div className="hidden sm:block">
                            <Table
                                style={{
                                    width: table.getTotalSize(),
                                }}
                            >
                                <PrimitiveTableHeader
                                    style={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: STICKY_HEADER_WRAPPER_Z_INDEX,
                                    }}
                                >
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                const {column} = header;
                                                const ariaSort = resolveAriaSort(header);
                                                return (
                                                    <TableHead
                                                        key={header.id}
                                                        className={"relative sticky z-20 top-0"}
                                                        style={{...getCommonPinningStyles(column, {isHeader: true})}}
                                                        aria-sort={ariaSort}
                                                    >
                                                        {renderHeaderContent(header)}
                                                        <div
                                                            onDoubleClick={() => header.column.resetSize()}
                                                            onMouseDown={header.getResizeHandler()}
                                                            onTouchStart={header.getResizeHandler()}
                                                            className={cn(
                                                                "resizer",
                                                                table.options.columnResizeDirection,
                                                                header.column.getIsResizing() ? "isResizing" : ""
                                                            )}
                                                            role="presentation"
                                                        />
                                                    </TableHead>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </PrimitiveTableHeader>

                                <TableBody>
                                    {isLoading ? (
                                        <TableRow className={'hover:bg-white'}>
                                            <TableCell
                                                colSpan={columns.length}
                                                className={scrollContainerClass}
                                            >
                                                <div className="py-4">
                                                    <div className="flex justify-center w-screen max-w-full">
                                                        <Loader />
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {table.getRowModel().rows?.length ? (
                                                table.getRowModel().rows.map((row) => (
                                                    <TableRow
                                                        className={cn(onClickRow && " cursor-pointer", "bg-white")}
                                                        key={row.id}
                                                        onClick={() => {
                                                            if (onClickRow) {
                                                                onClickRow(row.original.id);
                                                            }
                                                        }}
                                                        data-state={row.getIsSelected() && "selected"}
                                                    >
                                                        {row.getVisibleCells().map((cell) => {
                                                            const {column} = cell;

                                                            return (
                                                                <TableCell
                                                                    key={cell.id}
                                                                    style={{...getCommonPinningStyles(column)}}
                                                                    className={cn(
                                                                        column.getIsPinned() && !row.getIsSelected() && "transition-colors group-hover:bg-gray-200 bg-white",
                                                                        column.getIsPinned() && row.getIsSelected() && " transition-colors bg-gray-200",
                                                                    )}
                                                                >
                                                                    {flexRender(
                                                                        cell.column.columnDef.cell,
                                                                        cell.getContext()
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={columns.length}
                                                        className="h-24 bg-white text-center"
                                                    >
                                                        {t("empty_state.no_content")}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </InfiniteScroll>
                ) : (
                    <>
                        <DataTableMobile
                            table={table}
                            isLoading={isLoading}
                            resolveColumnLabel={resolveColumnLabel}
                            onClickRow={onClickRow}
                        />
                        <div className="hidden sm:block">
                            <Table
                                className={"min-w-full"}
                                style={{
                                    width: table.getTotalSize(),
                                }}
                            >
                                <PrimitiveTableHeader
                                    style={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: STICKY_HEADER_WRAPPER_Z_INDEX,
                                    }}
                                >
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                const {column} = header;
                                                const ariaSort = resolveAriaSort(header);
                                                return (
                                                    <TableHead
                                                        key={header.id}
                                                        style={{...getCommonPinningStyles(column, {isHeader: true})}}
                                                        aria-sort={ariaSort}
                                                    >
                                                        {renderHeaderContent(header)}
                                                        <div
                                                            onDoubleClick={() => header.column.resetSize()}
                                                            onMouseDown={header.getResizeHandler()}
                                                            onTouchStart={header.getResizeHandler()}
                                                            className={cn(
                                                                "resizer",
                                                                table.options.columnResizeDirection,
                                                                header.column.getIsResizing() ? "isResizing" : ""
                                                            )}
                                                            role="presentation"
                                                        />
                                                    </TableHead>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </PrimitiveTableHeader>

                                <TableBody>
                                    {isLoading ? (
                                        <TableRow className={'hover:bg-white'}>
                                            <TableCell
                                                colSpan={columns.length}
                                                className="h-52 text-center"
                                            >
                                                <div className={cn("w-full py-4 flex justify-center")}>
                                                    Loading...
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {table.getRowModel().rows?.length ? (
                                                table.getRowModel().rows.map((row) => (
                                                    <TableRow
                                                        className={cn(onClickRow && " cursor-pointer", "bg-white")}
                                                        key={row.id}
                                                        onClick={() => {
                                                            if (onClickRow) {
                                                                onClickRow(row.original.id);
                                                            }
                                                        }}
                                                        data-state={row.getIsSelected() && "selected"}
                                                    >
                                                        {row.getVisibleCells().map((cell) => {
                                                            const {column} = cell;

                                                            return (
                                                                <TableCell
                                                                    key={cell.id}
                                                                    style={{...getCommonPinningStyles(column)}}
                                                                    className={cn(
                                                                        column.getIsPinned() && !row.getIsSelected() && "transition-colors group-hover:bg-gray-200 bg-white",
                                                                        column.getIsPinned() && row.getIsSelected() && " transition-colors bg-gray-200",
                                                                    )}
                                                                >
                                                                    {flexRender(
                                                                        cell.column.columnDef.cell,
                                                                        cell.getContext()
                                                                    )}
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={columns.length}
                                                        className="h-24 text-center"
                                                    >
                                                        {t("empty_state.no_content")}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </div>


            {
                showPagination && (
                    <Pagination
                        isLoading={isLoading}
                        total={totalItems}
                        limit={pageSize}
                        offset={offset}
                        onChange={(offset) => {
                            // const nextPageIndex = Math.floor(offset / Math.max(1, pageSize));
                            // setPagination((prev) => ({ ...prev, pageIndex: nextPageIndex }));
                            // if (onChangeLimitOffset) {
                            //     onChangeLimitOffset(offset, pageSize);
                            // }
                        }}
                    />

                )
            }

            {enableColumnReorder && (
                <FormPopup
                    title={t("app.common.reorder_columns", {defaultValue: "Riordina colonne"})}
                    isOpen={isColumnReorderOpen}
                    setIsOpen={setIsColumnReorderOpen}
                    defaultWith="max-w-lg"
                >
                    {columnReorderItems.length > 0 ? (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">
                                {t("app.common.reorder_columns_hint", {
                                    defaultValue: "Trascina le colonne per cambiare l'ordine. Le modifiche vengono salvate automaticamente.",
                                })}
                            </p>
                            <DragDropContext onDragEnd={handleColumnReorderDragEnd}>
                                <Droppable droppableId="column-reorder-list">
                                    {(dropProvided) => (
                                        <div className="max-h-64 overflow-y-auto pr-1">
                                            <ul
                                                ref={dropProvided.innerRef}
                                                {...dropProvided.droppableProps}
                                                className="space-y-2"
                                            >
                                                {columnReorderItems.map((item, index) => (
                                                    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                                        {(dragProvided, dragSnapshot) => (
                                                            <li
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                style={{
                                                                    ...dragProvided.draggableProps.style,
                                                                }}
                                                                className={cn(
                                                                    "flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700",
                                                                    dragSnapshot.isDragging && "shadow-md ring-1 ring-blue-200",
                                                                )}
                                                            >
                                                                <span className="flex items-center gap-3">
                                                                    <span
                                                                        {...dragProvided.dragHandleProps}
                                                                        className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600"
                                                                    >
                                                                        <DragIcon className="h-4 w-4" />
                                                                    </span>
                                                                    <span className="font-medium text-gray-800">{item.label}</span>
                                                                </span>
                                                                {!item.isVisible && (
                                                                    <span className="text-xs text-gray-400">
                                                                        {t("app.common.hidden", {defaultValue: "Nascosta"})}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {dropProvided.placeholder}
                                            </ul>
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            {t("app.common.no_columns_available", {defaultValue: "Non ci sono colonne da ordinare."})}
                        </p>
                    )}
                </FormPopup>
            )}

        </div>
    )
}
